import {ChangeDetectorRef, Component, inject, OnDestroy, OnInit} from "@angular/core";
import {ActivatedRoute, NavigationEnd, Router} from "@angular/router";
import {Store} from "@ngrx/store";
import {Actions, ofType} from "@ngrx/effects";
import {FundActions, FundNameActions, FundValuationActions} from "../store/fund.actions";
import {
    isFundPageLoading,
    selectBaselineFund,
    selectFundError,
    selectFundIsArchived,
    selectFundName,
    selectFundTemplateContext,
    selectIsAnyFundFormInEditMode,
    selectRelatedDeal,
    selectSelectedFundId
} from "../store/fund/fund.selectors";
import {CommentCountsActions, CommentThreadActions} from "../../comment/store/comment.actions";
import {selectRequestedOpenContext} from "../../comment/store/comment.selectors";
import {CARD_COMMENT_CONTEXT_FACTORY, CARD_COMMENT_CONTEXT_PREFIX} from "../../shared/comments/card-context-menu/card-comment-context";
import {isCommentDeepLinkNavigation} from "../../shared/comments/comment-deep-link";
import {createFundCardCommentContextFactory} from "./fund-card-comment-context.factory";
import {combineLatest, distinctUntilChanged, filter, first, map, race, Subscription, switchMap, take} from "rxjs";
import {FormBuilder, Validators} from "@angular/forms";
import {TraceableDate} from "../../shared/model/traceable";
import {MatDialog} from "@angular/material/dialog";
import {MatSnackBar} from "@angular/material/snack-bar";
import {selectBaselineFundReport, selectReportDate, selectSelectedFundReport} from "../store/fund-report/fund-report.selectors";
import {FundReportNewDialogComponent} from "./fund-report-new-dialog/fund-report-new-dialog.component";
import {FundValuationManageDialogComponent} from "./fund-valuation-manage-dialog/fund-valuation-manage-dialog.component";
import {selectIsUndrawnComplete, selectSelectedFundValuation} from "../store/fund-valuation/fund-valuation.selectors";
import {selectMergeError, selectSelectedSourceDataset} from "../../import/store/import.selectors";
import {selectSelectedFundGeneralPartner} from "../store/general-partner/general-partner.selectors";
import {GeneralPartnerNewDialogComponent} from "../general-partner-new-dialog/general-partner-new-dialog.component";
import {ConfirmationComponent} from "../../shared/components/confirmation/confirmation.component";
import {FundValuationDescriptionDialogComponent} from "./fund-valuation-description-dialog/fund-valuation-description-dialog.component";
import {DocumentGeneratorComponent} from "../../shared/components/document-generator/document-generator.component";
import {CodeTableEnum} from "../../shared/model/code";
import {FundValuationTypeEnum} from "../models/fund-valuation-type.enum";
import {PatternAssignmentComponent} from "../../shared/components/pattern-group/pattern-assignment/pattern-assignment.component";
import {MergePreviewDialogComponent} from "./merge-preview-dialog/merge-preview-dialog.component";
import {FundAiSetupDialogComponent} from "../../fund-ai-setup/fund-ai-setup-dialog/fund-ai-setup-dialog.component";
import {
    ALGORITHMIC_DMV_DIALOG_RESULT_CREATE_VALUATION,
    AlgorithmicDmvAssetRow,
    AlgorithmicDmvDialogComponent,
    AlgorithmicDmvDialogResult,
    GpQuality
} from "../../algorithmic-dmv/algorithmic-dmv-dialog/algorithmic-dmv-dialog.component";
import {AlgorithmicDmvService} from "../../algorithmic-dmv/services/algorithmic-dmv.service";
import {FundValuationCreateDialogComponent} from "./fund-valuation-create-dialog/fund-valuation-create-dialog.component";
import {selectPartnershipInvestment} from "../store/fund/fund.selectors";
import {selectSettingsLoaded, selectSettingValue} from "../../shared/user-setting/store/user-setting.selectors";
import {SellerPositionActions} from "../../deal/store/deal.actions";

/** Setting key for the default required (expected) gross IRR applied to asset discount-multiple valuations. */
const ASSET_REQUIRED_GROSS_IRR_DEFAULT_KEY = "asset.requiredGrossIRR.default";

@Component({
    selector: "valumize-fund-details-page",
    templateUrl: "./fund-details-page.component.html",
    styleUrls: ["./fund-details-page.component.scss"],
    standalone: false,
    providers: [
        // Page-root comment-context prefix: one bulk-counts call per page covers every card under it.
        // Used by anchor-only cards (fund-details); scoped cards resolve through the factory below.
        {
            provide: CARD_COMMENT_CONTEXT_PREFIX,
            useFactory: (store: Store) =>
                store.select(selectSelectedFundId).pipe(map((id) => id != null ? `/funds/fund_id=${id}` : null)),
            deps: [Store],
        },
        // Scope-aware factory: report / report+valuation / +deal scoped cards key their thread to the
        // ids identifying their data (spec §3), resolved from the fund page's live selection.
        {
            provide: CARD_COMMENT_CONTEXT_FACTORY,
            useFactory: (store: Store) => createFundCardCommentContextFactory(store),
            deps: [Store],
        },
    ],
})
export class FundDetailsPageComponent implements OnInit, OnDestroy {
    private readonly store = inject(Store);
    private readonly formBuilder = inject(FormBuilder);
    private readonly dialog = inject(MatDialog);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly algorithmicDmvService = inject(AlgorithmicDmvService);
    private readonly actions$ = inject(Actions);
    private readonly snackBar = inject(MatSnackBar);
    protected readonly fundValuationTypeEnum = FundValuationTypeEnum;

    /**
     * Default "Expected gross IRR" for the algorithmic DMV dialog, as a percentage (e.g. 25),
     * resolved from the asset.requiredGrossIRR.default setting (stored as a fraction → ×100).
     * Undefined until settings load / when the setting is unset; the dialog then falls back to 25%.
     */
    private defaultGrossIrrPercent?: number;

    fundId?: number;
    fundReportId?: number;
    fundValuationId?: number;
    dealId?: number;
    fundReportDate?: TraceableDate;
    fundNameIsEditable = false;
    highlightFundReportDate = true;
    highlightFundClosingDate = true;
    searchEnabled = false;

    isLoading$ = this.store.select(isFundPageLoading);
    fundError$ = this.store.select(selectFundError);
    fundName$ = this.store.select(selectFundName);
    baselineFund$ = this.store.select(selectBaselineFund);
    fundReport$ = this.store.select(selectSelectedFundReport);
    baselineFundReport$ = this.store.select(selectBaselineFundReport);
    fundValuation$ = this.store.select(selectSelectedFundValuation);
    isUndrawnComplete$ = this.store.select(selectIsUndrawnComplete);
    deal$ = this.store.select(selectRelatedDeal);
    selectedGeneralPartner$ = this.store.select(selectSelectedFundGeneralPartner);
    reportDate$ = this.store.select(selectReportDate);
    sourceDataset$ = this.store.select(selectSelectedSourceDataset);
    isEditDisabled$ = this.store.select(selectIsAnyFundFormInEditMode);
    isArchived$ = this.store.select(selectFundIsArchived);
    mergeError$ = this.store.select(selectMergeError);

    codeTableFundValuationType = CodeTableEnum.FUND_VALUATIONTYPE;
    codeTableGpQuality = CodeTableEnum.FUND_GENERALPARTNERQUALITY;

    subscriptions: Subscription[] = [];

    nameForm = this.formBuilder.group({
        fundName: this.formBuilder.control("", {nonNullable: true, validators: [Validators.required, Validators.maxLength(100)]}),
    });

    constructor() {
        this.subscriptions.push(this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.reloadPage();
            }
        }));
    }

    ngOnInit() {
        this.fundId = parseInt(this.route.snapshot.paramMap.get("fundId") ?? "", 10);

        // One bulk-counts call per page (never per card — perf rule 1), reloaded when the entity id OR
        // the selected dataset changes so the has-comment icons match the current dataset.
        this.subscriptions.push(
            combineLatest([
                this.store.select(selectSelectedFundId).pipe(filter((id): id is number => id != null)),
                this.store.select(selectSelectedSourceDataset)
            ]).pipe(
                distinctUntilChanged((a, b) => a[0] === b[0] && a[1] === b[1])
            ).subscribe(([id]) =>
                this.store.dispatch(CommentCountsActions.load({inContext: `/funds/fund_id=${id}`}))
            )
        );

        this.subscriptions.push(
            this.store.select(selectFundName).pipe(map((nameApi) => {
                this.fundNameIsEditable = nameApi.fundNameIsEditable;
                this.nameForm.patchValue({
                    fundName: nameApi.name?.text
                });
                if (nameApi.fundNameIsEditable) {
                    this.nameForm.enable();
                } else {
                    this.nameForm.disable();
                }
                this.cdr.detectChanges();
            })).subscribe());

        this.subscriptions.push(
            this.store.select(selectReportDate).pipe(map((fundReportDate) => {
                this.fundReportDate = fundReportDate;
            })).subscribe());

        // Resolve the configured default expected gross IRR for the algorithmic DMV dialog. The
        // setting stores a fraction (e.g. 0.25); the dialog edits a percentage, so convert ×100.
        this.subscriptions.push(
            this.store.select(selectSettingsLoaded).pipe(
                filter(Boolean),
                take(1),
                switchMap(() => this.store.select(selectSettingValue(ASSET_REQUIRED_GROSS_IRR_DEFAULT_KEY)).pipe(take(1)))
            ).subscribe(value => {
                const fraction = typeof value === "number" ? value : Number(value);
                if (Number.isFinite(fraction)) {
                    this.defaultGrossIrrPercent = fraction * 100;
                }
            }));
    }

    reloadPage(): void {
        this.fundId = parseInt(this.route.snapshot.paramMap.get("fundId") ?? "", 10);
        this.fundReportId = this.getQueryParam("fundReportId");
        this.fundValuationId = this.getQueryParam("fundValuationId");
        this.dealId = this.getQueryParam("dealId");
        // Seller-position deep-links additionally carry seller_position_id (notification path) — see
        // CommentDeepLinkBuilder / ContextLinkUtil. The inbox path supplies it via the pending context below.
        const sellerPositionIdFromQuery = this.getQueryParam("sellerPositionId");

        // this.store.dispatch(FundActions.open({
        //     fundId: this.fundId,
        //     fundReportId: this.fundReportId,
        //     fundValuationId: this.fundValuationId,
        //     dealId: this.dealId
        // }));

        // A comment deep-link (inbox / notification) restores its context through the store, not query
        // params. When one targets this fund and the URL carries no report id, suppress the "new fund
        // report" dialog and preselect the target's report + valuation so its card renders and
        // CardContextMenuDirective can open the thread.
        //
        // Read the pending context synchronously here rather than from a field: reloadPage() runs from
        // the constructor's NavigationEnd subscription, which fires before ngOnInit on a fresh navigation,
        // so a field populated in ngOnInit would still be empty. store.select emits the current state
        // synchronously on subscribe, so take(1) gives the value that requestOpen set before navigating.
        let pendingContext: string | null = null;
        this.store.select(selectRequestedOpenContext).pipe(take(1)).subscribe(context => pendingContext = context);

        // #3 Option C: only a navigation certified as a comment deep-link may consume the one-shot
        // requestedOpenContext. A normal navigation carrying a stale one-shot (an earlier deep-link that
        // never matched a card) clears it here so it can't suppress this page's new-fund-report dialog.
        const navigationState = this.router.lastSuccessfulNavigation?.extras.state ?? history.state;
        const isCommentDeepLink = isCommentDeepLinkNavigation(navigationState);
        if (!isCommentDeepLink) {
            if (pendingContext) {
                this.store.dispatch(CommentThreadActions.openHandled());
            }
            pendingContext = null;
        }

        const pendingTarget = this.parseFundCommentTarget(pendingContext);
        const suppressNewFundReportDialog = !this.fundReportId && pendingTarget != null;
        if (!this.fundReportId && pendingTarget != null) {
            if (pendingTarget.fundReportId != null) {
                this.fundReportId = pendingTarget.fundReportId;
            }
            if (pendingTarget.fundValuationId != null) {
                this.fundValuationId = pendingTarget.fundValuationId;
            }
        }
        // Deal + seller position come from the URL on the notification path and from the pending
        // context on the inbox path (whose URL has no ids). Restoring the deal drives relatedDeal
        // (→ selectFundContext.dealId); the seller-position load below sets selectSelectedSellerPositionId.
        this.dealId ??= pendingTarget?.dealId;
        const sellerPositionId = sellerPositionIdFromQuery ?? pendingTarget?.sellerPositionId;

        this.store.dispatch(FundActions.loadFundData({
            fundId: this.fundId,
            fundReportId: this.fundReportId,
            fundValuationId: this.fundValuationId,
            dealId: this.dealId
        }));

        // Seller-position card deep-link: its context is scoped by deal + seller position, which no
        // other flow selects on the fund page. Load the seller position for this deal + valuation so
        // selectFundContext rebuilds the exact context the card is anchored to and its thread opens.
        if (sellerPositionId != null && this.dealId != null && this.fundValuationId != null) {
            this.store.dispatch(SellerPositionActions.load({dealId: this.dealId, fundValuationId: this.fundValuationId}));
        }

        if (this.fundReportId) {
            this.highlightFundReportDate = true;
            setTimeout(() => this.highlightFundReportDate = false, 3000); // revert back after 3 seconds
        } else if (!suppressNewFundReportDialog) {
            this.openNewFundReportDialog();
        }
        // else {
        //     this.checkAndLoadFundReport();
        // }
        if (this.fundValuationId) {
            this.highlightFundClosingDate = true;
            setTimeout(() => this.highlightFundClosingDate = false, 3000); // revert back after 3 seconds
        }
        // TODO: #21721 Load Fund Valuation only if not in merge mode
        // else if (this.fundReportId && !this.fundValuationId) {
        //     this.checkAndLoadFundValuation();
        // }
    }

    getQueryParam(queryParamToFind: string): number | undefined {
        const queryParam = this.route.snapshot.queryParamMap.get(queryParamToFind);
        return queryParam ? parseInt(queryParam, 10) : undefined;
    }

    /**
     * Parses a pending comment-open context
     * (`/funds/fund_id=F[/fund_report_id=R][/fund_valuation_id=V][/deal_id=D][/seller_position_id=SP]/…`)
     * when it targets *this* fund. Under the scoping redesign most fund cards are report + valuation
     * scoped (the seller-position card adds deal + seller position), so each id is read from its own
     * segment wherever it appears (not positionally). Returns the optional ids (all undefined for a
     * page-level fund comment), or null if there is no pending context or it targets another object.
     */
    private parseFundCommentTarget(context: string | null):
        { fundReportId?: number; fundValuationId?: number; dealId?: number; sellerPositionId?: number } | null {
        if (!context) {
            return null;
        }
        const fundMatch = /^\/funds\/fund_id=(\d+)/.exec(context);
        if (!fundMatch || parseInt(fundMatch[1], 10) !== this.fundId) {
            return null;
        }
        const parseSegment = (re: RegExp): number | undefined => {
            const match = re.exec(context);
            return match ? parseInt(match[1], 10) : undefined;
        };
        return {
            fundReportId: parseSegment(/\/fund_report_id=(\d+)/),
            fundValuationId: parseSegment(/\/fund_valuation_id=(\d+)/),
            dealId: parseSegment(/\/deal_id=(\d+)/),
            sellerPositionId: parseSegment(/\/seller_position_id=(\d+)/),
        };
    }

    scrollToComponent = (componentId: string) => document.getElementById(componentId)?.scrollIntoView({behavior: "auto"});

    editFundName = () => this.store.dispatch(FundNameActions.edit());

    saveFundName() {
        if (this.nameForm.valid) {
            this.store.dispatch(FundNameActions.save(this.nameForm.getRawValue()));
        }
    }

    cancelFundName = () => this.store.dispatch(FundNameActions.cancel());

    // checkAndLoadFundReport() {
    //     if (this.fundReportId === undefined) {
    //         this.subscriptions.push(this.store.select(selectSelectedFundReportsForFund).pipe(
    //             map((fundReports) => {
    //                 if (fundReports.length === 1) {
    //                     this.router.navigate([], {
    //                         relativeTo: this.route,
    //                         queryParams: {fundReportId: fundReports[0].id},
    //                         queryParamsHandling: "merge",
    //                         skipLocationChange: false,
    //                         replaceUrl: false
    //                     });
    //                 } else if (fundReports.length > 1) {
    //                     this.openNewFundReportDialog();
    //                 }
    //             })
    //         ).subscribe());
    //     }
    // }

    openEditFundValuationDescriptionDialog() {
        if (this.dialog.openDialogs.length === 0) {
            const dialogRef = this.dialog.open(FundValuationDescriptionDialogComponent);
            dialogRef.componentInstance.fundId = this.fundId;
            dialogRef.componentInstance.fundReportId = this.fundReportId;
        }
    }

    openNewFundReportDialog() {
        if (this.dialog.openDialogs.length === 0) {
            // Sized by its content, not fixed (WI-1239 "Last fixes"): the dialog defaults to two columns, so a
            // hard 1300x650 left most of the panel empty. The caps keep it on screen once the user turns on
            // enough of the 14 columns to outgrow the viewport; the table itself scrolls (see the component scss).
            const dialogRef = this.dialog.open(FundReportNewDialogComponent, {maxWidth: "95vw", maxHeight: "90vh"});
            dialogRef.componentInstance.fundId = this.fundId;
            dialogRef.componentInstance.currentFundReportId = this.fundReportId
        }
    }

    // checkAndLoadFundValuation() {
    //     if (this.fundValuationId === undefined) {
    //         this.openNewFundValuationDialog();
    //     }
    // }

    openNewFundValuationDialog() {
        if (this.dialog.openDialogs.length === 0) {
            const dialogRef = this.dialog.open(FundValuationManageDialogComponent, {width: "850px", height: "650px"});
            dialogRef.componentInstance.fundId = this.fundId;
            dialogRef.componentInstance.fundReportId = this.fundReportId;
            dialogRef.componentInstance.currentFundValuationId = this.fundValuationId;
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    displaySearch() {
        this.searchEnabled = !this.searchEnabled;
    }

    selectFundMergeTarget(searchResultReference: string) {
        const regex = /^\/funds\/(\d+)$/;
        const match = searchResultReference.match(regex);
        if (match) {
            const mergeTargetId = Number(match[1]);
            this.store.dispatch(FundActions.setMergeTarget({mergeTargetId}));
        }
    }

    deselectFundMergeTarget() {
        this.store.dispatch(FundActions.setMergeTarget({mergeTargetId: undefined}));
    }

    openMergePreview(): void {
        this.dialog.open(MergePreviewDialogComponent, {
            width: "auto",
            maxWidth: "calc(100vw - 64px)",
            disableClose: true,
        });
    }

    openNewGeneralPartnerDialog() {
        if (this.dialog.openDialogs.length === 0) {
            const dialogRef = this.dialog.open(GeneralPartnerNewDialogComponent);
            dialogRef.componentInstance.fundId = this.fundId;
        }
    }

    onFundMenuButtonClick(event: Event): void {
        // Remove focus from the button to avoid the aria-hidden conflict. Without this, the focused button is hidden and triggers an accessibility error.
        (event.currentTarget as HTMLElement).blur();
    }

    openArchiveConfirmation(archived: boolean): void {
        const dialogRef = this.dialog.open(ConfirmationComponent);
        dialogRef.componentInstance.confirmMessage = `Are you sure you want to ${archived ? "archive" : "unarchive"} this fund?`;

        dialogRef.afterClosed().pipe(
            map(result => {
                if (result && this.fundId != null) {
                    this.store.dispatch(FundActions.archive({archived}));
                }
            })
        ).subscribe();
    }

    openDocumentGeneratorDialog() {
        this.store.select(selectFundTemplateContext)
            .pipe(first(context => !!context))
            .subscribe(context => {
                const dialogRef = this.dialog.open(DocumentGeneratorComponent);
                dialogRef.componentInstance.context = context;
            });
    }

    openPatternAssignments() {
        this.dialog.open(PatternAssignmentComponent);
    }

    openAiSetupDialog() {
        if (this.fundId == null) return;
        const fundId = this.fundId;
        this.store.select(selectPartnershipInvestment)
            .pipe(first(pi => !!pi?.data?.partnershipInvestmentRecords?.length))
            .subscribe(pi => {
                const assets = pi.data.partnershipInvestmentRecords.map(r => ({
                    id: r.assetId,
                    name: r.company?.text || `Asset ${r.assetId}`,
                    isHeld: !r.isRealized?.value
                }));
                this.dialog.open(FundAiSetupDialogComponent, {
                    data: {fundId, assets},
                    width: "880px",
                    maxWidth: "95vw"
                });
            });
    }

    /**
     * Opens the algorithmic DMV selection dialog. When the fund's current report
     * has no Bottom-Up valuation, the dialog renders its empty-state and closes
     * with {@link ALGORITHMIC_DMV_DIALOG_RESULT_CREATE_VALUATION}; we then chain
     * into the existing create-valuation flow.
     */
    openAlgorithmicDmvDialog() {
        if (this.fundId == null) return;
        const fundId = this.fundId;
        const fundValuationId = this.fundValuationId ?? null;

        // The GP's current quality pre-fills the dialog's required dropdown. The codes
        // are LOW/MEDIUM/HIGH (matching GeneralPartnerQuality); anything else (or unset)
        // becomes null so the dialog forces the user to choose.
        this.selectedGeneralPartner$.pipe(first()).subscribe(gp => {
            // hasGp drives whether the chosen quality can be saved back (no GP → run-only).
            const hasGp = gp?.id != null;
            const code = gp?.quality?.code;
            const gpQuality: GpQuality | null =
                code === "LOW" || code === "MEDIUM" || code === "HIGH" ? code : null;

            if (fundValuationId === null) {
                // No valuation active in the page — let the user pick (or create) one via the
                // standard Manage Fund Valuation dialog, then run the DMV against the chosen one.
                this.pickValuationThenRunDmv(fundId, hasGp, gpQuality);
                return;
            }

            this.algorithmicDmvService.getAssetSelection(fundId, fundValuationId).subscribe({
                next: rows => this.openAlgorithmicDmvDialogWith(fundId, fundValuationId, rows, hasGp, gpQuality),
                error: err => this.notifyAssetSelectionFailed(err)
            });
        });
    }

    /** User-friendly feedback when the algorithmic DMV asset-selection fetch fails (was a silent console.error). */
    private notifyAssetSelectionFailed(err: unknown) {
        console.error("Failed to load algorithmic DMV asset selection", err);
        this.snackBar.open("Couldn't load the assets for the algorithmic DMV. Please try again.", "OK", {duration: 6000});
    }

    /**
     * Reuses the standard Manage Fund Valuation dialog as the picker when no valuation is
     * active. Its select-and-return mode (returnToAsset) closes with the chosen valuation id,
     * which we then run the algorithmic DMV against — so the picker looks exactly like the
     * "+ Fund Valuation" dialog the user already knows. No report context → fall back to the
     * DMV dialog's own create-only empty-state.
     */
    private pickValuationThenRunDmv(fundId: number, hasGp: boolean, gpQuality: GpQuality | null) {
        if (this.fundReportId == null) {
            this.openAlgorithmicDmvDialogWith(fundId, null, [], hasGp, gpQuality);
            return;
        }
        const ref = this.dialog.open<FundValuationManageDialogComponent, unknown, number | undefined>(
            FundValuationManageDialogComponent, {width: "850px", height: "650px"});
        ref.componentInstance.fundId = fundId;
        ref.componentInstance.fundReportId = this.fundReportId;
        ref.componentInstance.currentFundValuationId = this.fundValuationId;
        // returnToAsset = the dialog's "select and return the id (don't navigate)" mode.
        ref.componentInstance.returnToAsset = true;
        // Tell the user why this dialog opened — it's reused as the DMV's valuation picker.
        ref.componentInstance.title = "Select a fund valuation for the Algorithmic DMV";
        ref.componentInstance.contextMessage =
            "The algorithmic DMV runs inside a Bottom-Up fund valuation. Pick one to run against, or create a new one.";

        const runDmvFor = (fundValuationId: number) =>
            this.algorithmicDmvService.getAssetSelection(fundId, fundValuationId).subscribe({
                next: rows => this.openAlgorithmicDmvDialogWith(fundId, fundValuationId, rows, hasGp, gpQuality),
                error: err => this.notifyAssetSelectionFailed(err)
            });

        // Resolve the valuation id deterministically from whichever path produces one first:
        //   • user creates a valuation → FundValuationActions.createSuccess fires (the manage dialog
        //     then closes with no value via the global close-all effect)
        //   • user picks an existing one → afterClosed emits the chosen id
        // race() takes the first real id and drops the other source; both are filtered to non-null,
        // so a plain cancel (afterClosed === undefined) simply never runs the DMV. This avoids the
        // previous race where takeUntil(afterClosed) could tear down the createSuccess listener
        // before it fired (the close-all effect emits afterClosed on the same createSuccess action).
        this.subscriptions.push(
            race(
                this.actions$.pipe(
                    ofType(FundValuationActions.createSuccess),
                    map(action => action.fundValuationWithScenarios.fundValuation.id),
                    filter((id): id is number => id != null)
                ),
                ref.afterClosed().pipe(filter((id): id is number => id != null))
            ).pipe(take(1)).subscribe(runDmvFor)
        );
    }

    private openAlgorithmicDmvDialogWith(
        fundId: number,
        fundValuationId: number | null,
        rows: { assetId: number; assetName: string;
                existingDmvSource: "NONE" | "ALGORITHM" | "USER";
                isEligible: boolean; ineligibilityReason?: string;
                nav?: number; unrealizedTvpi?: number; totalTvpi?: number }[],
        hasGp: boolean,
        gpQuality: GpQuality | null
    ) {
        const assets: AlgorithmicDmvAssetRow[] = rows.map(r => ({
            id: r.assetId,
            name: r.assetName,
            existingDmvSource: r.existingDmvSource,
            isEligible: r.isEligible,
            ineligibilityReason: r.ineligibilityReason,
            nav: r.nav,
            unrealizedTvpi: r.unrealizedTvpi,
            totalTvpi: r.totalTvpi
        }));

        const ref = this.dialog.open<AlgorithmicDmvDialogComponent, unknown, AlgorithmicDmvDialogResult>(
            AlgorithmicDmvDialogComponent, {
                data: {fundId, fundValuationId, assets, hasGp, gpQuality, defaultGrossIrrPercent: this.defaultGrossIrrPercent},
                width: "880px",
                maxWidth: "95vw"
            });

        ref.afterClosed().subscribe(result => {
            if (result === ALGORITHMIC_DMV_DIALOG_RESULT_CREATE_VALUATION
                && this.fundId != null && this.fundReportId != null) {
                // Chain into the existing create-valuation flow. After it closes the
                // user can re-open the menu — we don't auto-reopen because the new
                // valuation needs to be selected in the report state first.
                this.dialog.open(FundValuationCreateDialogComponent, {
                    data: {fundId: this.fundId, fundReportId: this.fundReportId}
                });
            }
        });
    }
}
